import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js'
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js'
import typefaceFont from 'three/examples/fonts/helvetiker_regular.typeface.json'
import { Line2 } from 'three/addons/lines/Line2.js';
import { LineMaterial } from 'three/addons/lines/LineMaterial.js';
import { LineGeometry } from 'three/addons/lines/LineGeometry.js';
import { randomFloat, randomInteger } from '../utils'
import { FPSController } from '../fpsController'
import positionColorVertexShader from './shaders/positionVertexColor.glsl'
import positionColorFragmentShader from './shaders/positionFragmentColor.glsl'

const rSlider = document.getElementById('rSlider');
rSlider.style.setProperty('--SliderColor', `rgb(100%, 0%, 0%)`);
const gSlider = document.getElementById('gSlider');
gSlider.style.setProperty('--SliderColor', `rgb(0%, 100%, 0%)`);
const bSlider = document.getElementById('bSlider');
bSlider.style.setProperty('--SliderColor', `rgb(0%, 0%, 100%)`);
const rValueDiv = document.getElementById("rValue");
const gValueDiv = document.getElementById("gValue");
const bValueDiv = document.getElementById("bValue");
const hSlider = document.getElementById('hSlider');
const sSlider = document.getElementById('sSlider');
const lSlider = document.getElementById('lSlider');
const hValueDiv = document.getElementById("hValue");
const sValueDiv = document.getElementById("sValue");
const lValueDiv = document.getElementById("lValue");
const hexValueInput = document.getElementById("hexValue");
const sortSelect = document.getElementById("colorsOrder");

let pickerColor = new THREE.Color(81/255, 8/255, 154/255);
rSlider.value = pickerColor.r * 255;
gSlider.value = pickerColor.g * 255;
bSlider.value = pickerColor.b * 255;

const CUBESIZE = 150;
const CUBEHALFSIZE = CUBESIZE * 0.5;

let needRedraw = true;

function updateSliderBackgrounds() {
    const hsl = pickerColor.getHSL(pickerColor);
    const r = Math.trunc(pickerColor.r * 100);
    const g = Math.trunc(pickerColor.g * 100);
    const b = Math.trunc(pickerColor.b * 100);
    const h = Math.trunc(hsl.h * 360);
    const s = Math.trunc(hsl.s * 100);
    const l = Math.trunc(hsl.l * 100);

    rSlider.style.setProperty('--SliderGradientBegin', `rgb(0%, ${g}%, ${b}%), rgb(100%, ${g}%, ${b}%)`);
    gSlider.style.setProperty('--SliderGradientBegin', `rgb(${r}%, 0%, ${b}%), rgb(${r}%, 100%, ${b}%)`);
    bSlider.style.setProperty('--SliderGradientBegin', `rgb(${r}%, ${g}%, 0%), rgb(${r}%, ${g}%, 100%)`);

    const hueStops = Array.from({ length: 11 }, (_, i) => `hsl(${i * 36}, ${s}%, ${l}%)`).join(', ');
    hSlider.style.setProperty('--SliderGradientBegin', hueStops);

    const satStops = Array.from({ length: 11 }, (_, i) => `hsl(${h}, ${i * 10}%, ${l}%)`).join(', ');
    sSlider.style.setProperty('--SliderGradientBegin', satStops);

    const lightStops = Array.from({ length: 11 }, (_, i) => `hsl(${h}, ${s}%, ${i * 10}%)`).join(', ');
    lSlider.style.setProperty('--SliderGradientBegin', lightStops);
}

const colors = Object.keys(THREE.Color.NAMES);
const htmlColorNames = {};
for (let index = 0; index < colors.length; index++) {
    const colorName = colors[index];
    const color = new THREE.Color();
    color.setColorName(colorName.toLowerCase());
    const hexValue = color.getHexString();
    if (hexValue in htmlColorNames) {
        continue;
    }
    htmlColorNames[hexValue] = colorName;
}

function updatePicker() {
    pickerColor.r = rSlider.value / 255;
    pickerColor.g = gSlider.value / 255;
    pickerColor.b = bSlider.value / 255;
    updateLabelsAndSliders();
    updateSphere(spherePicker, pickerColor);
    updateSphereGizmos();
    updateSliderBackgrounds();
    if (sliderLines.length > 0) updateSliderLines();
    needRedraw = true;
}

function updatePickerHSL() {
    pickerColor.setHSL(hSlider.value / 255, sSlider.value / 255, lSlider.value / 255);
    updateLabelsAndSliders();
    updateSphere(spherePicker, pickerColor);
    updateSphereGizmos();
    updateSliderBackgrounds();
    if (sliderLines.length > 0) updateSliderLines();
    needRedraw = true;
}

function updateSphereGizmos() {
    redCube.scale.setX(pickerColor.r * CUBESIZE);
    greenCube.scale.setY(pickerColor.g * CUBESIZE);
    blueCube.scale.setZ(pickerColor.b * CUBESIZE);

    redCube.position.setX(pickerColor.r * CUBEHALFSIZE - CUBEHALFSIZE);
    greenCube.position.setY(pickerColor.g * CUBEHALFSIZE - CUBEHALFSIZE);
    blueCube.position.setZ(pickerColor.b * CUBEHALFSIZE - CUBEHALFSIZE);
}

function updateLabelsAndSliders() {
    rSlider.value = pickerColor.r * 255;
    gSlider.value = pickerColor.g * 255;
    bSlider.value = pickerColor.b * 255;

    const hsl = pickerColor.getHSL(pickerColor);

    hSlider.value = hsl.h * 255;
    sSlider.value = hsl.s * 255;
    lSlider.value = hsl.l * 255;

    rValueDiv.innerHTML = rSlider.value;
    gValueDiv.innerHTML = gSlider.value;
    bValueDiv.innerHTML = bSlider.value;
    hValueDiv.innerHTML = hSlider.value;
    sValueDiv.innerHTML = sSlider.value;
    lValueDiv.innerHTML = lSlider.value;
    hexValueInput.value = pickerColor.getHexString();
}

function updateColorsFromHex() {
    pickerColor.setHex(parseInt(hexValueInput.value, 16));
    updateLabelsAndSliders();
}

rSlider.oninput = function () { updatePicker(); };
gSlider.oninput = function () { updatePicker(); };
bSlider.oninput = function () { updatePicker(); };
hSlider.oninput = function () { updatePickerHSL(); };
sSlider.oninput = function () { updatePickerHSL(); };
lSlider.oninput = function () { updatePickerHSL(); };
hexValueInput.oninput = function () { updateColorsFromHex(); };

const scene = new THREE.Scene();
scene.background = pickerColor;

const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
};

window.addEventListener('resize', () => {
    sizes.width = window.innerWidth;
    sizes.height = window.innerHeight;

    camera.aspect = sizes.width / sizes.height;
    camera.updateProjectionMatrix();

    renderer.setSize(sizes.width, sizes.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    updateSceneScale();

    for (const line of sliderLines) {
        line.material.resolution.set(sizes.width, sizes.height);
    }
});

let currentSelectionColor = null;

let isDragging = false;
let mouseDownPos = { x: 0, y: 0 };
const DRAG_THRESHOLD = 5;

window.addEventListener('mousedown', (event) => {
    mouseDownPos.x = event.clientX;
    mouseDownPos.y = event.clientY;
    isDragging = false;
});

window.addEventListener('mousemove', (event) => {
    mouse.x = event.clientX / sizes.width * 2 - 1;
    mouse.y = -(event.clientY / sizes.height) * 2 + 1;

    const dx = event.clientX - mouseDownPos.x;
    const dy = event.clientY - mouseDownPos.y;
    if (dx * dx + dy * dy > DRAG_THRESHOLD * DRAG_THRESHOLD) {
        isDragging = true;
    }
});

window.addEventListener('click', () => {
    if (isDragging) return;
    if (hoveredSliderLineIndex !== -1) {
        const sliderIntersects = raycaster.intersectObjects(sliderLines);
        if (sliderIntersects.length > 0) {
            const point = sliderIntersects[0].point;
            const newColor = positionToColor(point);
            pickerColor.r = newColor.r;
            pickerColor.g = newColor.g;
            pickerColor.b = newColor.b;
            updateLabelsAndSliders();
            updateSphere(spherePicker, pickerColor);
            updateSphereGizmos();
            updateSliderBackgrounds();
            if (sliderLines.length > 0) updateSliderLines();
            needRedraw = true;
        }
        return;
    }
    if (currentSelectionColor != null) {
        const color = new THREE.Color();
        color.setColorName(currentSelectionColor.toLowerCase());
        pickerColor = color;
        updateLabelsAndSliders();
        updatePicker();
    }
});

const fpsController = new FPSController();

const mouse = new THREE.Vector2();

const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 1000);

const canvas = document.querySelector('#c');
const renderer = new THREE.WebGLRenderer({ canvas });
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.localClippingEnabled = true;
const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;
controls.enableZoom = false;

const BASE_BG_RADIUS = 135;
const BASE_ORBIT_RADIUS = 72;
const BASE_TEXT_RADIUS_INNER = 59;
const BASE_TEXT_RADIUS_OUTER = 69;
let backgroundSphere = null;
let orbitScale = 1;

function updateSceneScale() {
    const dist = camera.position.distanceTo(controls.target);
    const vHalf = THREE.MathUtils.degToRad(camera.fov / 2);
    const hHalf = Math.atan(camera.aspect * Math.tan(vHalf));

    // Background sphere: match the orbit circle's apparent size
    const orbitRadius = BASE_ORBIT_RADIUS * orbitScale;
    const orbitAngle = Math.atan(orbitRadius / 100);
    const bgRadius = dist * Math.sin(orbitAngle) * 1.05;
    if (backgroundSphere) {
        backgroundSphere.scale.setScalar(bgRadius / BASE_BG_RADIUS);
    }

    // Orbit: shrink to fit within the smaller viewport dimension
    const vExtent = 100 * Math.tan(vHalf);
    const hExtent = 100 * Math.tan(hHalf);
    orbitScale = Math.min(vExtent, hExtent) / vExtent;
}

const raycaster = new THREE.Raycaster();
raycaster.params.Line2 = { threshold: 8 };

camera.position.z = 0;
camera.position.x = -221;
camera.position.y = 30;
camera.lookAt(0, 0, 0);

const positionColorMaterial = new THREE.ShaderMaterial({
    vertexShader: positionColorVertexShader,
    fragmentShader: positionColorFragmentShader
});

const spherePicker = addSphereAxis(pickerColor, 5, false);
addSphereBorder(spherePicker, 6, false);

const cubes = addSphereGizmo(spherePicker);
const redCube = cubes[0];
const greenCube = cubes[1];
const blueCube = cubes[2];

function addBackgroundSphere() {
    const geometry = new THREE.SphereGeometry(BASE_BG_RADIUS, 24, 24);
    const material = new THREE.MeshBasicMaterial({ color: new THREE.Color('black'), side: THREE.BackSide });
    backgroundSphere = new THREE.Mesh(geometry, material);
    scene.add(backgroundSphere);
}

function addSphereGizmo(sphere) {
    const geometry = new THREE.BoxGeometry(1, 1, 1);

    const greenMaterial = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
    const redMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 });
    const blueMaterial = new THREE.MeshBasicMaterial({ color: 0x0000ff });

    const redCube = new THREE.Mesh(geometry, redMaterial);
    const greenCube = new THREE.Mesh(geometry, greenMaterial);
    const blueCube = new THREE.Mesh(geometry, blueMaterial);

    const width = 3;
    redCube.scale.set(CUBESIZE, width, width);
    greenCube.scale.set(width, CUBESIZE, width);
    blueCube.scale.set(width, width, CUBESIZE);

    redCube.position.set(0, -CUBEHALFSIZE, -CUBEHALFSIZE);
    greenCube.position.set(-CUBEHALFSIZE, 0, -CUBEHALFSIZE);
    blueCube.position.set(-CUBEHALFSIZE, -CUBEHALFSIZE, 0);

    scene.add(redCube);
    scene.add(greenCube);
    scene.add(blueCube);

    return [redCube, greenCube, blueCube];
}

function addText(font, text, positionIn, color, parent) {
    const matDark = new THREE.LineBasicMaterial({
        color: color,
        side: THREE.DoubleSide
    });

    const shapes = font.generateShapes(text, 1.5);
    const geometry = new THREE.ShapeGeometry(shapes);
    const mesh = new THREE.Mesh(geometry, matDark);
    mesh.position.copy(positionIn);
    parent.add(mesh);

    return mesh;
}

function colorToPosition(color) {
    return new THREE.Vector3(
        -CUBEHALFSIZE + color.r * CUBESIZE,
        -CUBEHALFSIZE + color.g * CUBESIZE,
        -CUBEHALFSIZE + color.b * CUBESIZE
    );
}

function positionToColor(position) {
    return new THREE.Color(
        (position.x + CUBEHALFSIZE) / CUBESIZE,
        (position.y + CUBEHALFSIZE) / CUBESIZE,
        (position.z + CUBEHALFSIZE) / CUBESIZE
    );
}

function updateSphere(sphere, color) {
    sphere.material.color = color;
    sphere.position.set(
        -CUBEHALFSIZE + color.r * CUBESIZE,
        -CUBEHALFSIZE + color.g * CUBESIZE,
        -CUBEHALFSIZE + color.b * CUBESIZE
    );
    scene.background = color;
}

function addSphereBorder(parentSphere, size, over = false) {
    const geometry = new THREE.SphereGeometry(size, 8, 8);
    const material = new THREE.MeshBasicMaterial({ color: new THREE.Color('white'), side: THREE.BackSide });
    const sphere = new THREE.Mesh(geometry, material);
    parentSphere.add(sphere);

    if (over) {
        sphere.renderOrder = 998;
        sphere.material.depthTest = false;
        sphere.material.transparent = true;
    }
}

function addSphereAxis(color, size, over = false) {
    const geometry = new THREE.SphereGeometry(size, 8, 8);
    const material = new THREE.MeshBasicMaterial({ color: color });
    const sphere = new THREE.Mesh(geometry, material);
    updateSphere(sphere, color);
    scene.add(sphere);

    if (over) {
        sphere.renderOrder = 999;
        sphere.material.depthTest = false;
        sphere.material.transparent = true;
    }

    return sphere;
}

function loaded(font) {
    createGrid();
    addColors(font);
    addBackgroundSphere();
    updateSceneScale();
    initSliderLines();
    sortSelect.value = "HexString";
    sortSelect.onchange();
    updatePicker();
    addColorParticles();
}

const loaderText = new FontLoader();
loaderText.load('/fonts/helvetiker_regular.typeface.json', function (font) {
    loaded(font);
});

function clampPosition(position) {
    position.clampScalar(-CUBEHALFSIZE, CUBEHALFSIZE);
    return position;
}

function addVerticesAndColors(mesh, positions, colors, position) {
    clampPosition(position);
    const color = positionToColor(position);
    positions.push(position.x, position.y, position.z);
    colors.push(color.r, color.g, color.b);
}

function createGrid() {
    const origin = new THREE.Vector3(-CUBEHALFSIZE, -CUBEHALFSIZE, -CUBEHALFSIZE);
    const end = new THREE.Vector3(CUBEHALFSIZE, CUBEHALFSIZE, CUBEHALFSIZE);
    const line = addCubeFromLines(5);
    updateCubePositions(line, origin, end, false);
}

function updateCubePositions(mesh, startPoint, endPoint, addDiagonals) {
    const zeroPoint = startPoint;
    const onePoint = new THREE.Vector3(endPoint.x, startPoint.y, startPoint.z);
    const twoPoint = new THREE.Vector3(endPoint.x, startPoint.y, endPoint.z);
    const threePoint = new THREE.Vector3(startPoint.x, startPoint.y, endPoint.z);
    const fourPoint = new THREE.Vector3(startPoint.x, endPoint.y, startPoint.z);
    const fivePoint = new THREE.Vector3(endPoint.x, endPoint.y, startPoint.z);
    const sixPoint = endPoint;
    const sevenPoint = new THREE.Vector3(startPoint.x, endPoint.y, endPoint.z);

    const vertices = [];
    const colors = [];

    addVerticesAndColors(mesh, vertices, colors, zeroPoint);
    addVerticesAndColors(mesh, vertices, colors, onePoint);
    addVerticesAndColors(mesh, vertices, colors, twoPoint);
    addVerticesAndColors(mesh, vertices, colors, threePoint);
    addVerticesAndColors(mesh, vertices, colors, zeroPoint);

    addVerticesAndColors(mesh, vertices, colors, fourPoint);
    addVerticesAndColors(mesh, vertices, colors, fivePoint);
    addVerticesAndColors(mesh, vertices, colors, sixPoint);
    addVerticesAndColors(mesh, vertices, colors, sevenPoint);
    addVerticesAndColors(mesh, vertices, colors, fourPoint);

    addVerticesAndColors(mesh, vertices, colors, sevenPoint);
    addVerticesAndColors(mesh, vertices, colors, threePoint);
    addVerticesAndColors(mesh, vertices, colors, twoPoint);
    addVerticesAndColors(mesh, vertices, colors, sixPoint);
    addVerticesAndColors(mesh, vertices, colors, fivePoint);
    addVerticesAndColors(mesh, vertices, colors, onePoint);

    if (addDiagonals) {
        addVerticesAndColors(mesh, vertices, colors, fourPoint);
        addVerticesAndColors(mesh, vertices, colors, threePoint);
        addVerticesAndColors(mesh, vertices, colors, sixPoint);
        addVerticesAndColors(mesh, vertices, colors, onePoint);
        addVerticesAndColors(mesh, vertices, colors, fivePoint);
        addVerticesAndColors(mesh, vertices, colors, zeroPoint);
        addVerticesAndColors(mesh, vertices, colors, sevenPoint);
        addVerticesAndColors(mesh, vertices, colors, twoPoint);
        addVerticesAndColors(mesh, vertices, colors, fivePoint);
    }

    mesh.geometry.setPositions(vertices);
    mesh.geometry.setColors(colors);
}

function addCubeFromLines(linewidth, vertexCount = 2) {
    const vertices = [], colors = [];

    for (let i = 0; i < vertexCount; i++) {
        for (let j = 0; j < 3; j++) {
            vertices.push(randomFloat(-CUBEHALFSIZE, CUBEHALFSIZE));
            colors.push(0);
        }
    }

    const geometry = new LineGeometry();
    geometry.setPositions(vertices);
    geometry.setColors(colors);

    const matLine = new LineMaterial({
        color: 0xffffff,
        linewidth: linewidth,
        vertexColors: true,
        dashed: false,
        alphaToCoverage: true,
    });

    const line = new Line2(geometry, matLine);
    line.computeLineDistances();
    line.scale.set(1, 1, 1);
    scene.add(line);

    return line;
}

const SLIDER_LINE_SEGMENTS = 200;
const SLIDER_LINE_WIDTH = 3;
const SLIDER_LINE_WIDTH_HOVER = 8;
const sliderLines = [];
let hoveredSliderLineIndex = -1;
const sliderTableRows = document.querySelectorAll('table tr');

function createSliderLine(linewidth) {
    const vertexCount = SLIDER_LINE_SEGMENTS + 1;
    const vertices = new Array(vertexCount * 3).fill(0);
    const colors = new Array(vertexCount * 3).fill(0);

    const geometry = new LineGeometry();
    geometry.setPositions(vertices);
    geometry.setColors(colors);

    const matLine = new LineMaterial({
        color: 0xffffff,
        linewidth: linewidth,
        vertexColors: true,
        dashed: false,
        alphaToCoverage: false,
        resolution: new THREE.Vector2(sizes.width, sizes.height),
    });

    const line = new Line2(geometry, matLine);
    line.computeLineDistances();
    scene.add(line);
    return line;
}

function initSliderLines() {
    for (let i = 0; i < 6; i++) {
        sliderLines.push(createSliderLine(SLIDER_LINE_WIDTH));
    }
}

function updateSliderLines() {
    const vertexCount = SLIDER_LINE_SEGMENTS + 1;
    const hsl = {};
    pickerColor.getHSL(hsl);

    for (let lineIndex = 0; lineIndex < 6; lineIndex++) {
        const positions = [];
        const colors = [];

        for (let i = 0; i < vertexCount; i++) {
            const t = i / SLIDER_LINE_SEGMENTS;
            let color;

            switch (lineIndex) {
                case 0: // Red
                    color = new THREE.Color(t, pickerColor.g, pickerColor.b);
                    break;
                case 1: // Green
                    color = new THREE.Color(pickerColor.r, t, pickerColor.b);
                    break;
                case 2: // Blue
                    color = new THREE.Color(pickerColor.r, pickerColor.g, t);
                    break;
                case 3: // Hue
                    color = new THREE.Color();
                    color.setHSL(t, hsl.s, hsl.l);
                    break;
                case 4: // Saturation
                    color = new THREE.Color();
                    color.setHSL(hsl.h, t, hsl.l);
                    break;
                case 5: // Lightness
                    color = new THREE.Color();
                    color.setHSL(hsl.h, hsl.s, t);
                    break;
            }

            const pos = colorToPosition(color);
            positions.push(pos.x, pos.y, pos.z);
            colors.push(color.r, color.g, color.b);
        }

        sliderLines[lineIndex].geometry.setPositions(positions);
        sliderLines[lineIndex].geometry.setColors(colors);
        sliderLines[lineIndex].computeLineDistances();
    }
}

const sortOrigin = new THREE.Vector3();

function sortByDistanceToAxis(aArray, bArray) {
    const a = aArray[0];
    const b = bArray[0];
    const vectorA = new THREE.Vector3(a.material.color.r, a.material.color.g, a.material.color.b);
    const vectorB = new THREE.Vector3(b.material.color.r, b.material.color.g, b.material.color.b);
    return vectorA.distanceTo(sortOrigin) - vectorB.distanceTo(sortOrigin);
}

function sortByName(aArray, bArray) {
    return aArray[1].localeCompare(bArray[1]);
}

function sortByHexString(aArray, bArray) {
    return aArray[0].material.color.getHexString().localeCompare(bArray[0].material.color.getHexString());
}

sortSelect.onchange = function () {
    let sortingAlgorithm = sortByDistanceToAxis;
    switch (sortSelect.value) {
        case "Alphabetic":
            sortingAlgorithm = sortByName;
            break;
        case "HexString":
            sortingAlgorithm = sortByHexString;
            break;
        case "White":
            sortOrigin.set(1, 1, 1);
            break;
        case "Black":
            sortOrigin.set(0, 0, 0);
            break;
        case "Red":
            sortOrigin.set(1, 0, 0);
            break;
        case "Green":
            sortOrigin.set(0, 1, 0);
            break;
        case "Blue":
            sortOrigin.set(0, 0, 1);
            break;
        case "Turquoise":
            sortOrigin.set(0, 1, 1);
            break;
        case "Yellow":
            sortOrigin.set(1, 1, 0);
            break;
        case "Magenta":
            sortOrigin.set(1, 0, 1);
            break;
    }
    borderColors.sort(sortingAlgorithm);
};

const borderColors = [];
let colorMaterial = null;

function addColors(font) {
    const hexValues = Object.keys(htmlColorNames);
    for (let index = 0; index < hexValues.length; index++) {
        const hexValue = hexValues[index];
        const colorName = htmlColorNames[hexValue];

        const color = new THREE.Color();
        color.setHex(parseInt(hexValue, 16));
        const colorSphere = addSphereAxis(color, 2, false);
        addSphereBorder(colorSphere, 2.1, false);

        const colorSphereAround = addSphereAxis(color, 1.5, true);
        addText(font, colorName, new THREE.Vector3(0, 0, 0), color, colorSphereAround);
        addSphereBorder(colorSphereAround, 1.7, true);
        borderColors.push([colorSphereAround, colorName, colorSphere]);
    }

    borderColors.sort(sortByName);
}

function addColorParticles() {
    const geometry = new THREE.BufferGeometry();

    const particlesSide = 40;
    const particlesCount = Math.pow(particlesSide, 3);
    const positions = new Float32Array(particlesCount * 3);

    let index = 0;
    for (let i = 0; i < particlesSide; i++) {
        for (let j = 0; j < particlesSide; j++) {
            for (let k = 0; k < particlesSide; k++) {
                const position = new THREE.Vector3(
                    -75 + i * 150 / (particlesSide - 1),
                    -75 + j * 150 / (particlesSide - 1),
                    -75 + k * 150 / (particlesSide - 1)
                );

                positions[index++] = position.x;
                positions[index++] = position.y;
                positions[index++] = position.z;
            }
        }
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    colorMaterial = new THREE.ShaderMaterial({
        vertexShader: positionColorVertexShader,
        fragmentShader: positionColorFragmentShader,
        transparent: true,
        depthWrite: true,
        blending: THREE.NoBlending,
        vertexColors: true,
        uniforms: {
            uTime: { value: 0 },
            pickerPosition: { value: new THREE.Vector3(0) }
        },
    });

    const points = new THREE.Points(geometry, colorMaterial);
    points.frustumCulled = false;
    scene.add(points);
}

const cameraWorldDirection = new THREE.Vector3();
const clock = new THREE.Clock();

const tick = () => {
    const elapsedTime = clock.getElapsedTime();

    if (colorMaterial) {
        colorMaterial.uniforms.uTime.value = elapsedTime;
        colorMaterial.uniforms.pickerPosition.value = colorToPosition(pickerColor);
    }

    fpsController.tick(elapsedTime);
    controls.update();

    const delta = clock.getDelta() * 1000;

    camera.getWorldDirection(cameraWorldDirection);
    cameraWorldDirection.multiplyScalar(100);
    const radius = BASE_ORBIT_RADIUS * orbitScale;

    const angleSeparation = 2 * Math.PI / borderColors.length;
    const intersectCircleObjects = [];
    const intersectCenterObjects = [];
    currentSelectionColor = null;

    for (let index = 0; index < borderColors.length; index++) {
        const opposite = Math.sin(angleSeparation * index) * radius;
        const adjacent = Math.cos(angleSeparation * index) * radius;
        const newPosition = camera.localToWorld(new THREE.Vector3(opposite, adjacent, 0)).clone().add(cameraWorldDirection);

        const currentBorderColor = borderColors[index];
        const mesh = currentBorderColor[0];
        mesh.position.copy(newPosition);

        let offset = Math.PI / 2;
        let radiusText = BASE_TEXT_RADIUS_INNER * orbitScale;
        if (index > borderColors.length * 0.5) {
            offset *= -1;
            radiusText = BASE_TEXT_RADIUS_OUTER * orbitScale;
        }
        const oppositeText = Math.sin(angleSeparation * index) * radiusText;
        const adjacentText = Math.cos(angleSeparation * index) * radiusText;
        const newPositionText = camera.localToWorld(new THREE.Vector3(oppositeText, adjacentText, 0)).clone().add(cameraWorldDirection);
        const parentPosition = new THREE.Vector3();
        mesh.getWorldPosition(parentPosition);
        newPositionText.sub(parentPosition);

        const textChildren = mesh.children[0];
        textChildren.position.copy(newPositionText);
        textChildren.quaternion.copy(camera.quaternion);
        textChildren.rotation.z = camera.rotation.z + offset - angleSeparation * index;

        const borderMesh = mesh.children[1];
        mesh.scale.set(1, 1, 1);
        intersectCircleObjects.push(borderMesh);

        const centralColorMesh = currentBorderColor[2];
        intersectCenterObjects.push(centralColorMesh);
        centralColorMesh.scale.set(1, 1, 1);
    }

    raycaster.setFromCamera(mouse, camera);

    // Slider line hover detection
    hoveredSliderLineIndex = -1;
    for (let i = 0; i < sliderLines.length; i++) {
        sliderLines[i].material.linewidth = SLIDER_LINE_WIDTH;
        if (sliderTableRows[i]) sliderTableRows[i].classList.remove('slider-highlight');
    }
    if (sliderLines.length > 0) {
        const sliderIntersects = raycaster.intersectObjects(sliderLines);
        if (sliderIntersects.length > 0) {
            const hitLine = sliderIntersects[0].object;
            hoveredSliderLineIndex = sliderLines.indexOf(hitLine);
            if (hoveredSliderLineIndex !== -1) {
                sliderLines[hoveredSliderLineIndex].material.linewidth = SLIDER_LINE_WIDTH_HOVER;
                if (sliderTableRows[hoveredSliderLineIndex]) {
                    sliderTableRows[hoveredSliderLineIndex].classList.add('slider-highlight');
                }
            }
        }
    }

    if (borderColors.length > 0) {
        const intersects = raycaster.intersectObjects(intersectCircleObjects);

        if (intersects.length > 0) {
            for (const intersect of intersects) {
                const index = intersectCircleObjects.findIndex((element) => element === intersect.object);
                if (index != -1) {
                    borderColors[index][0].scale.set(1.75, 1.75, 1.75);
                    borderColors[index][2].scale.set(5, 5, 5);
                    currentSelectionColor = borderColors[index][1];
                }
            }
        }
        const intersectsCenter = raycaster.intersectObjects(intersectCenterObjects);
        if (intersectsCenter.length > 0) {
            for (const intersect of intersectsCenter) {
                const index = intersectCenterObjects.findIndex((element) => element === intersect.object);
                if (index != -1) {
                    borderColors[index][0].scale.set(1.75, 1.75, 1.75);
                    borderColors[index][2].scale.set(5, 5, 5);
                    currentSelectionColor = borderColors[index][1];
                }
            }
        }
    }

    renderer.render(scene, camera);
    window.requestAnimationFrame(tick);
};

tick();
